import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from './cn';
import { Text } from './Typography';

// Tabs is a closed primitive — consumers declare the full set as data
// (label + content per tab) and the primitive owns the structure end
// to end. There is no `TabsList` / `TabsTrigger` / `TabsContent` in
// the public surface; the iteration over `items` is internal.
//
// Closed shape because Tabs is a uniform list of (label, content)
// pairs — composition flexibility (which other compound primitives
// like Dialog and Popover need) doesn't add value here, but does add
// surface area and ways to wire the wrong `value` to the wrong panel.

const TABS_LIST_CLASSNAME = 'tw-inline-flex tw-h-11 tw-items-center tw-justify-center tw-rounded-lg tw-bg-muted tw-p-1 tw-text-muted-foreground';

const TABS_TRIGGER_CLASSNAME = 'tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-md tw-px-3 tw-py-1.5 tw-text-muted-foreground tw-transition-all focus:tw-outline-none focus:tw-ring-0 focus-visible:tw-outline-none focus-visible:tw-ring-0 disabled:tw-pointer-events-none disabled:tw-opacity-50 data-[state=active]:tw-bg-background data-[state=active]:tw-text-foreground data-[state=active]:tw-shadow-none';

const TABS_CONTENT_CLASSNAME = 'tw-mt-3 focus:tw-outline-none focus:tw-ring-0';

const Tabs = ({
    className: _className,
    style: _style,
    defaultValue,
    items,
    onValueChange,
    value,
    ...props
}) => {
    const resolvedDefaultValue = defaultValue ?? items[0]?.value;

    return (
        <TabsPrimitive.Root
            defaultValue={resolvedDefaultValue}
            onValueChange={onValueChange}
            value={value}
            {...props}
        >
            <TabsPrimitive.List className={cn(TABS_LIST_CLASSNAME)}>
                {items.map(item => (
                    <TabsPrimitive.Trigger
                        className={cn(TABS_TRIGGER_CLASSNAME)}
                        disabled={item.disabled}
                        key={item.value}
                        value={item.value}
                    >
                        <Text tone="inherit" variant="control">{item.label}</Text>
                    </TabsPrimitive.Trigger>
                ))}
            </TabsPrimitive.List>
            {items.map(item => (
                <TabsPrimitive.Content
                    className={cn(TABS_CONTENT_CLASSNAME)}
                    key={item.value}
                    value={item.value}
                >
                    {item.content}
                </TabsPrimitive.Content>
            ))}
        </TabsPrimitive.Root>
    );
};

Tabs.displayName = 'Tabs';

export { Tabs };
